import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Calendar, 
  MapPin, 
  ChevronLeft, 
  ChevronRight, 
  Heart, 
  Luggage 
} from 'lucide-react';
import './HeroSection.css';
import{useNavigate} from 'react-router-dom';

// Auto-sliding Hero Background & Header Content Data
const heroSlides = [
  {
    id: 1,
    title: 'Your Dream\nHoneymoon Awaits.',
    subtitle: 'Curated experiences for the first chapter of\nyour happily ever after.',
    bgImage: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1920&q=80',
  },
  {
    id: 2,
    title: 'Discover Luxury\nOverwater Haven.',
    subtitle: 'Private infinity pools, candlelight dining, and\nendless turquoise horizons.',
    bgImage: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=1920&q=80',
  },
  {
    id: 3,
    title: 'Unforgettable\nCoastal Getaways.',
    subtitle: 'Tailor-made itineraries designed exclusively\nfor romantic couples.',
    bgImage: 'https://images.unsplash.com/photo-1512100356356-de1b84283e18?auto=format&fit=crop&w=1920&q=80',
  },
];

const HeroSection = () => {
  // Carousel State for Fetched Packages
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  // Background & Hero Text Auto-Slider State
  const [heroIndex, setHeroIndex] = useState(0);

  // 1. Fetch API Data for Honeymoon Collections
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch('https://admin.jayrajgroup.com/api/honeymoon/packages');
        const result = await response.json();
        
        if (result.status && result.data) {
          setPackages(result.data);
        }
      } catch (error) {
        console.error('Error fetching honeymoon packages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  // 2. Auto-slide Background & Content every 6 seconds
  useEffect(() => {
    const heroTimer = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroSlides.length);
    }, 6000);

    return () => clearInterval(heroTimer);
  }, []);

  // Controls for Featured Collections Slider
  const handlePrev = () => {
    if (packages.length === 0) return;
    setCurrentIndex((prev) => (prev === 0 ? packages.length - 1 : prev - 1));
  };

  const handleNext = () => {
    if (packages.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % packages.length);
  };

  return (
    <section className="hb-hero-container">
      {/* Dynamic Auto-Sliding Background Images */}
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`hb-hero-bg ${index === heroIndex ? 'active' : ''}`}
          style={{ backgroundImage: `url(${slide.bgImage})` }}
        />
      ))}

      {/* Floating Animated Doodles */}
      <div className="hb-floating-doodle doodle-left">
        <Heart className="doodle-heart-left" size={20} />
      </div>

      <div className="hb-floating-doodle doodle-right">
        <svg viewBox="0 0 160 80" className="flight-path">
          <path d="M 10 70 Q 70 10 150 40" fill="none" stroke="#5c7882" strokeWidth="2" strokeDasharray="4,4" />
        </svg>
        <Luggage className="doodle-luggage" size={22} />
        <Heart className="doodle-heart-right" size={18} />
      </div>

      {/* Auto-Sliding Header Content */}
      <div className="hb-hero-content" key={heroSlides[heroIndex].id}>
        <h1 className="hb-title">
          {heroSlides[heroIndex].title.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i === 0 && <br />}
            </React.Fragment>
          ))}
        </h1>
        <p className="hb-subtitle">
          {heroSlides[heroIndex].subtitle.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i === 0 && <br />}
            </React.Fragment>
          ))}
        </p>

        {/* Search Bar */}
        <div className="hb-search-pill">
          <div className="hb-search-field">
            <span>Where to?</span>
            <span className="hb-dropdown-arrow">▼</span>
          </div>
          
          <div className="hb-field-divider" />
          
          <div className="hb-search-field">
            <span>When?</span>
            <Calendar size={18} className="hb-icon-cal" />
          </div>

          <button className="hb-search-btn">
            <span>Find Inspiration</span>
            <Search size={16} />
          </button>
        </div>
      </div>

      {/* Featured Honeymoon Collections API Carousel */}
      <div className="hb-collections-container">
        <div className="hb-collections-header">
          <h3>Featured Honeymoon Collections</h3>
          <div className="hb-slider-controls">
            <button onClick={handlePrev} aria-label="Previous Slide">
              <ChevronLeft size={16} />
            </button>
            <button onClick={handleNext} aria-label="Next Slide">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        <div className="hb-cards-viewport">
          {loading ? (
            <div className="hb-loading-text">Loading packages...</div>
          ) : (
            <div 
              className="hb-cards-track"
              style={{ transform: `translateX(-${currentIndex * 260}px)` }}
            >
              {packages.map((pkg) => (
                <div key={pkg.id} className="hb-collection-card" onClick={() => navigate(`/honeymoon/packages/${pkg.slug}`)}>
                  <img src={pkg.featured_photo_url} alt={pkg.name} />
                  <div className="hb-card-info">
                    <h4>{pkg.name}</h4>
                    <p className="hb-pkg-desc">{pkg.short_description}</p>
                    <div className="hb-card-footer">
                      <span className="hb-duration">
                        {pkg.duration_days}D / {pkg.duration_nights}N
                      </span>
                      <span className="hb-price">
                        ₹{pkg.plans_starting_from.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;